import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withKeys as _withKeys, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "text-lg font-semibold text-gray-900" }
const _hoisted_2 = { class: "grid grid-cols-4 gap-y-8 gap-x-4 mt-10 md:grid-cols-4" }
const _hoisted_3 = { class: "grid grid-cols-4 gap-y-8 gap-x-4 mt-8 md:grid-cols-4" }
const _hoisted_4 = { class: "grid grid-cols-4 mt-8" }
const _hoisted_5 = { class: "text-gray-500 text-sm" }
const _hoisted_6 = { class: "flex space-between mt-2" }
const _hoisted_7 = {
  key: 2,
  class: "pr-2 text-gray-500"
}
const _hoisted_8 = { class: "col-span-1 flex justify-end items-center" }

import { computed, PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseButton } from '@/lib/components/Button';
import { BaseIcon } from '@/lib/components/Icon';
import { BaseDatePicker } from '@/lib/components/Date';
import { BaseTextInput } from '@/lib/components/Input';
import { genderOptionsExternalLookup } from '@/constants';
import { formattedNHSNumber, isNHSNumberValid } from '@/lib/helpers/nhs-number.helper';
import dayjs from 'dayjs';
import { isValidUKPostcode } from '@/lib/helpers/uhb-postcode.helper';
import { v4 } from 'uuid';
import { ExternalPatientSearchParam } from '@/models';
import BaseSearchInput from '@/lib/components/Search/BaseSearchInput.vue';
import { IOption } from '@/lib';
import customParseFormat from 'dayjs/plugin/customParseFormat';

interface MpiPdsRequirement {
  label: string;
  isRequirementMet: boolean;
  isError: boolean;
}

type SearchType = 'MPI' | 'SPINE';


export default /*#__PURE__*/_defineComponent({
  __name: 'PatientSearchCard',
  props: {
  loading: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: Object as PropType<ExternalPatientSearchParam>,
    required: true
  },
  searchType: {
    type: String,
    required: true
  }
},
  emits: ["update:modelValue", "update-minimum-search-requirements-check", "search"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:modelValue'): void;
  (e: 'update-minimum-search-requirements-check'): void;
  (e: 'search'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;

dayjs.extend(customParseFormat);

const { t } = useI18n();

// TODO: please add emits values definition



const nhsNumberError = ref('');
const postcodeError = ref('');
const dobError = ref('');
const genderOptions = genderOptionsExternalLookup();
const masks = {
  input: 'YYYY-MM-DD'
};
const minAge = 16;

const isAnyError = computed<boolean>(() => !!(nhsNumberError.value || postcodeError.value || dobError.value));
const areMinimumSearchRequirementsMet = computed<boolean>(() => {
  if (mpiAndPdsRequirementFields.value[0]) {
    return mpiAndPdsRequirementFields.value[0].requirements.some((requirement) => requirement.isRequirementMet);
  }
  return false;
});
const mpiAndPdsRequirementFields = computed<Array<{
  label: string;
  type: SearchType;
  requirements: Array<MpiPdsRequirement>;
}>>(() => ([
  {
    label: t('custom.uhb.patient.mpi-requirements') as string,
    requirements: mpiRequirementFields.value,
    type: 'MPI' as SearchType
  },
  {
    label: t('custom.uhb.patient.pds-requirements') as string,
    requirements: pdsRequirementFields.value,
    type: 'SPINE' as SearchType
  }
].filter((requirement) => requirement.type === props.searchType)));
const mpiRequirementFields = computed<Array<MpiPdsRequirement>>(() => [
  {
    label: t('custom.uhb.patient.mpi-search-criteria-nhs') as string,
    isRequirementMet: isMpiPdsNhsNumberRequirementMet.value,
    isError: Boolean(nhsNumberError.value)
  },
  {
    label: t('custom.uhb.patient.mpi-search-criteria-demographics') as string,
    isRequirementMet: isMpiDobPlusOneRequirementMet.value,
    isError: Boolean(postcodeError.value)
  }
]);
const pdsRequirementFields = computed<Array<MpiPdsRequirement>>(() => [
  {
    label: t('custom.uhb.patient.pds-search-criteria-nhs-dob') as string,
    isRequirementMet: isMpiPdsNhsNumberRequirementMet.value && Boolean(props.modelValue.dob),
    isError: Boolean(nhsNumberError.value)
  },
  {
    label: t('custom.uhb.patient.pds-search-criteria-demographics') as string,
    isRequirementMet: isPdsFullNameSexDobRequirementMet.value,
    isError: false
  }
]);
const isPdsFullNameSexDobRequirementMet = computed<boolean>(() => !!(
  props.modelValue.givenName &&
  props.modelValue.familyName &&
  props.modelValue.gender &&
  props.modelValue.dob &&
  isFamilyNameMinimumLength.value &&
  isGivenNameMinimumLength.value
));
const isMpiPdsNhsNumberRequirementMet = computed<boolean>(() => !!props.modelValue.nhsNumber &&
  isNHSNumberValid(props.modelValue.nhsNumber)
);
const isMpiDobPlusOneRequirementMet = computed<boolean>(() => !!(
  props.modelValue.dob &&
  ((props.modelValue.givenName && isGivenNameMinimumLength.value) ||
    (props.modelValue.familyName && isFamilyNameMinimumLength.value) ||
    props.modelValue.gender ||
    props.modelValue.postcode)
));
const filteredGenderOptions = computed(() => genderOptions.filter((option: IOption) =>
  option.label.toLowerCase().includes((props.modelValue.gender || '').toLowerCase())
));
const isGivenNameMinimumLength = computed(() => {
  if (props.modelValue.givenName) {
    return props.modelValue.givenName.length > 1;
  }
  return false;
});
const isFamilyNameMinimumLength = computed(() => {
  if (props.modelValue.familyName) {
    return props.modelValue.familyName.length > 1;
  }
  return false;
});
const disableSearch = computed(() => (
  isAnyError.value ||
  !(
    props.modelValue.nhsNumber || // search by nhsNumber only
    (props.modelValue.nhsNumber && oneOfOptionalFieldIsReady.value) || // search by nhsNumber + one of the optional field
    (props.modelValue.dob &&
      dayjs(props.modelValue.dob, masks.input).isValid() &&
      oneOfOptionalFieldIsReady.value)
  ) // search by dob + one of the optional field
));
const oneOfOptionalFieldIsReady = computed(() =>
// one of the option field is ready and there's no postcode error.
  (props.modelValue.familyName || props.modelValue.givenName || props.modelValue.gender || props.modelValue.postcode) &&
    !postcodeError.value
);
const today = computed(() => dayjs().format('YYYY-MM-DD'));
const maxBirthDate = computed(() => dayjs().subtract(minAge, 'year').format('YYYY-MM-DD'));


watch(() => props.searchType, () => search());

watch(areMinimumSearchRequirementsMet, (value: boolean) => {
  emit('update-minimum-search-requirements-check', value);
});


const validateName = (ev: KeyboardEvent) => {
  const isValid = /^[a-z ,.'-]+$/i.test(ev.key);
  if (!isValid) {
    ev.preventDefault();
  }
  return isValid;
};
const validateDob = (dobValue: string) => {
  if (dobValue) {
    if (dobValue > maxBirthDate.value) {
      dobError.value = t('custom.uhb.patient.max-dob-error', [minAge]) as string;
    } else {
      dobError.value = '';
    }
  }

  if (dobValue === '') {
    dobError.value = '';
  }

  emit('update:modelValue', { ...props.modelValue, dob: dobValue });
};
const validatePostcode = (postcodeValue: string) => {
  if (postcodeValue) {
    postcodeError.value =
      isValidUKPostcode(postcodeValue) || !postcodeValue
        ? ''
        : (t('platform.patient.postcode-is-invalid') as string);
  } else {
    postcodeError.value = t('platform.patient.postcode-is-invalid') as string;
  }
  emit('update:modelValue', { ...props.modelValue, postcode: postcodeValue });

  if (postcodeValue === '') {
    postcodeError.value = '';
  }
};
const validateNHSNumber = (nhsNumberValue = '', eventType = '') => {
  if (nhsNumberValue && !isNHSNumberValid(nhsNumberValue)) {
    nhsNumberError.value = t('custom.uhb.patient.nhs-number-error') as string;
  } else {
    nhsNumberError.value = '';
  }

  if (eventType === 'blur') {
    emit('update:modelValue', {
      ...props.modelValue,
      nhsNumber: formattedNHSNumber(nhsNumberValue)
    });
  } else {
    emit('update:modelValue', { ...props.modelValue, nhsNumber: nhsNumberValue });
  }
};
const search = () => {
  const searchParams = {
    GUID: v4(),
    type: props.searchType,
    ...(dayjs(props.modelValue.dob, masks.input).isValid()
      ? { dob: dayjs(props.modelValue.dob, masks.input).format('YYYYMMDD') }
      : {}),
    ...(props.modelValue.givenName ? { givenName: props.modelValue.givenName } : {}),
    ...(props.modelValue.familyName ? { familyName: props.modelValue.familyName } : {}),
    ...(props.modelValue.gender ? { gender: props.modelValue.gender } : {}),
    ...(props.modelValue.postcode ? { postcode: props.modelValue.postcode } : {}),
    ...(props.modelValue.nhsNumber ? { nhsNumber: props.modelValue.nhsNumber.replace(/\s/g, '') } : {})
  };

  emit('search', searchParams);
};
const clear = () => {
  emit('update:modelValue', {
    nhsNumber: '',
    dob: '',
    givenName: '',
    familyName: '',
    gender: '',
    postcode: ''
  });
  nhsNumberError.value = '';
  postcodeError.value = '';
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("section", null, [
      _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('custom.uhb.patient.search')), 1),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(BaseTextInput), {
          "model-value": __props.modelValue.nhsNumber,
          class: "col-span-3",
          label: _ctx.$t('custom.uhb.patient.nhs-number'),
          placeholder: _ctx.$t('custom.uhb.patient.enter-nhs-number'),
          error: nhsNumberError.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (validateNHSNumber($event))),
          onBlur: _cache[1] || (_cache[1] = ($event: any) => (validateNHSNumber($event.target.value, $event.type))),
          onKeyup: _cache[2] || (_cache[2] = _withKeys(($event: any) => (!_unref(disableSearch) && search()), ["enter"]))
        }, null, 8, ["model-value", "label", "placeholder", "error"]),
        _createVNode(_unref(BaseDatePicker), {
          "model-value": __props.modelValue.dob,
          class: "col-span-1 leading-tight",
          "max-date": _unref(today),
          label: _ctx.$t('platform.patient.date-of-birth'),
          error: dobError.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (validateDob($event))),
          onKeyup: _cache[4] || (_cache[4] = _withKeys(($event: any) => (!_unref(disableSearch) && search()), ["enter"]))
        }, null, 8, ["model-value", "max-date", "label", "error"])
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(BaseTextInput), {
          "model-value": __props.modelValue.givenName,
          label: _ctx.$t('platform.patient.given-names'),
          placeholder: _ctx.$t('platform.patient.enter-given-names'),
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('update:modelValue', { ...__props.modelValue, givenName: $event }))),
          onKeypress: _cache[6] || (_cache[6] = ($event: any) => (validateName($event))),
          onKeyup: _cache[7] || (_cache[7] = _withKeys(($event: any) => (!_unref(disableSearch) && search()), ["enter"]))
        }, null, 8, ["model-value", "label", "placeholder"]),
        _createVNode(_unref(BaseTextInput), {
          "model-value": __props.modelValue.familyName,
          label: _ctx.$t('platform.patient.family-name'),
          placeholder: _ctx.$t('platform.patient.enter-family-name'),
          class: "leading-tight",
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('update:modelValue', { ...__props.modelValue, familyName: $event }))),
          onKeypress: _cache[9] || (_cache[9] = ($event: any) => (validateName($event))),
          onKeyup: _cache[10] || (_cache[10] = _withKeys(($event: any) => (!_unref(disableSearch) && search()), ["enter"]))
        }, null, 8, ["model-value", "label", "placeholder"]),
        _createVNode(BaseSearchInput, {
          "model-value": __props.modelValue.gender,
          label: _ctx.$t('platform.patient.gender'),
          placeholder: _ctx.$t('platform.patient.enter-gender'),
          suggestions: _unref(filteredGenderOptions),
          "max-width": false,
          class: "leading-tight",
          clear: "",
          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => (_ctx.$emit('update:modelValue', { ...__props.modelValue, gender: $event })))
        }, null, 8, ["model-value", "label", "placeholder", "suggestions"]),
        _createVNode(_unref(BaseTextInput), {
          "model-value": __props.modelValue.postcode,
          label: _ctx.$t('platform.patient.postcode'),
          placeholder: _ctx.$t('platform.patient.enter-postcode'),
          "is-optional": "",
          error: postcodeError.value,
          "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => (validatePostcode($event))),
          onKeyup: _cache[13] || (_cache[13] = _withKeys(($event: any) => (!_unref(disableSearch) && search()), ["enter"]))
        }, null, 8, ["model-value", "label", "placeholder", "error"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(mpiAndPdsRequirementFields), (requirementSection, sectionIndex) => {
          return (_openBlock(), _createElementBlock("div", {
            key: sectionIndex,
            class: "col-span-3"
          }, [
            _createElementVNode("div", _hoisted_5, _toDisplayString(requirementSection.label) + ": ", 1),
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(requirementSection.requirements, (requirement, requirementIndex) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: requirementIndex,
                  class: _normalizeClass(["flex space-x-2 text-xs items-center", {
                'text-success-500': requirement.isRequirementMet,
                'text-danger-500': requirement.isError,
                'text-gray-500': !requirement.isRequirementMet && !requirement.isError
              }])
                }, [
                  (!requirement.isError)
                    ? (_openBlock(), _createBlock(_unref(BaseIcon), {
                        key: 0,
                        name: "tick"
                      }))
                    : (_openBlock(), _createBlock(_unref(BaseIcon), {
                        key: 1,
                        name: "close"
                      })),
                  _createElementVNode("div", null, _toDisplayString(requirement.label), 1),
                  (requirementIndex !== requirementSection.requirements.length - 1)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, " or "))
                    : _createCommentVNode("", true)
                ], 2))
              }), 128))
            ])
          ]))
        }), 128)),
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_unref(BaseButton), {
            color: "ghost",
            onClick: clear
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('platform.common.clear')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(BaseButton), {
            class: "ml-2",
            disabled: !_unref(areMinimumSearchRequirementsMet) || _unref(disableSearch) || __props.loading || _unref(isAnyError),
            onClick: search
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('platform.common.search')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ])
    ])
  ]))
}
}

})